import {
  Session,
  User,
  useSession,
  useSupabaseClient,
} from "@supabase/auth-helpers-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { sendPasswordResetEmail } from "../api/auth.api";
import { useToastContext } from "./ToastContextWrapper";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthContextWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const supabase = useSupabaseClient();
  const session = useSession();
  const queryClient = useQueryClient();
  const { showToast } = useToastContext();

  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setUser(session?.user ?? null);
    setIsLoading(false);
  }, [session]);

  const resetPassword = useCallback(
    async (email: string) => {
      setIsLoading(true);

      try {
        await sendPasswordResetEmail({
          email: email.trim().toLowerCase(),
          redirectTo: `${window.location.origin}/password-recovery`,
          verificationType: "recovery",
        });

        showToast({
          title: "Password reset email sent",
          description: "Check your inbox for reset instructions.",
          type: "success",
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to send reset email.";
        showToast({ title: "Reset failed", description: message, type: "error" });
      } finally {
        setIsLoading(false);
      }
    },
    [showToast]
  );

  const updatePassword = useCallback(
    async (password: string) => {
      setIsLoading(true);

      try {
        const { error } = await supabase.auth.updateUser({ password });

        if (error) {
          showToast({
            title: "Update failed",
            description: error.message,
            type: "error",
          });
          return false;
        }

        showToast({
          title: "Password updated",
          description: "Your password has been updated successfully.",
          type: "success",
        });
        queryClient.invalidateQueries({ queryKey: ["user"] });
        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to update password.";
        showToast({
          title: "Update failed",
          description: message,
          type: "error",
        });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [queryClient, showToast, supabase]
  );

  const signOut = useCallback(async () => {
    setIsLoading(true);

    try {
      await supabase.auth.signOut();

      // Clear all React Query cached data so stale user data isn't served
      // to the next session.
      queryClient.clear();

      // Clear localStorage so Supabase auth tokens and any app state don't
      // persist after logout.
      localStorage.clear();
      sessionStorage.clear();

      showToast({
        title: "Signed out",
        description: "You have been signed out successfully.",
        type: "success",
      });
    } catch (err) {
      console.error("Error signing out:", err);
      showToast({
        title: "Sign out failed",
        description: "We couldn't sign you out. Please try again.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [queryClient, showToast, supabase]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      isAuthenticated: Boolean(user),
      isLoading,
      signOut,
      resetPassword,
      updatePassword,
    }),
    [isLoading, resetPassword, session, signOut, updatePassword, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuthContext must be used within an AuthContextWrapper");
  }

  return context;
};
